import type { CartItem, CartSnapshot, DiscountApplication } from '../../types/cartTypes';

type RecalcParams = {
  uiCart: CartSnapshot;
  lineRef: string;
  nextQty: number;
};

function getLineRef(item: CartItem): string {
  return item.properties?.__clientLineId ?? item.key;
}

function recalcLine(item: CartItem, nextQty: number): CartItem {
  const safeQty = Math.max(0, nextQty);
  const baseQuantity = item.quantity || 0;
  const unitFinalPrice = baseQuantity > 0
    ? item.final_line_price / baseQuantity
    : item.price;
  const unitOriginalPrice = baseQuantity > 0
    ? item.original_line_price / baseQuantity
    : item.original_price;

  const finalLinePrice = Math.round(unitFinalPrice * safeQty);
  const originalLinePrice = Math.round(unitOriginalPrice * safeQty);

  return {
    ...item,
    quantity: safeQty,
    final_line_price: finalLinePrice,
    original_line_price: originalLinePrice,
    line_level_total_discount: Math.max(0, originalLinePrice - finalLinePrice)
  };
}

function calcCartLevelDiscountTotal(discounts: DiscountApplication[]): number {
  return discounts.reduce(
    (sum, discount) => sum + (discount.total_allocated_amount || 0),
    0
  );
}

function recalcTotals(items: CartItem[], discounts: DiscountApplication[]) {
  let count = 0;
  let totalPrice = 0;
  let originalTotalPrice = 0;

  items.forEach((item) => {
    const qty = Math.max(0, item.quantity || 0);
    count += qty;
    totalPrice += item.final_line_price || 0;
    originalTotalPrice += item.original_line_price || 0;
  });

  const cartLevelDiscountTotal = calcCartLevelDiscountTotal(discounts);
  const total_price = Math.max(0, totalPrice - cartLevelDiscountTotal);
  const original_total_price = originalTotalPrice;
  const total_discount = Math.max(0, original_total_price - total_price);

  return {
    count,
    total_price,
    original_total_price,
    total_discount
  };
}

export function recalcUiCartAfterQtyChange(params: RecalcParams): CartSnapshot {
  const { uiCart, lineRef, nextQty } = params;
  let matched = false;

  const updatedItems = uiCart.items.map((item) => {
    if (getLineRef(item) !== lineRef) return item;
    matched = true;
    return recalcLine(item, nextQty);
  });

  if (!matched) return uiCart;

  const totals = recalcTotals(updatedItems, uiCart.cart_level_discount_applications);

  return {
    ...uiCart,
    items: updatedItems,
    count: totals.count,
    total_price: totals.total_price,
    original_total_price: totals.original_total_price,
    total_discount: totals.total_discount
  };
}
